import axios from 'axios';
import _isEmpty from 'lodash/isEmpty';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import { setInSession } from '../globalHelper';
import { OMNI_CHANNEL_API_SESSION_KEY, pageNames } from '../../common/constants';
import { isLoggedIn, getDeviceType, getCsrfToken, getIcfConfigMappings } from '../twHelper';
// eslint-disable-next-line no-unused-vars
import { updateContextFPTIhome, setContextFPTIhome, updateContextFPTIcontactUs } from '../analytics/homePageAnalytics';
import { trackActivityEvent } from '../analytics/fptiHelper';
import {
  ECM_DOC_ID_KEY,
  ECM_COMPONENT_IDS_KEY,
  ECM_DOC_ID_DEFAULT_VALUE,
  ECM_COMPONENT_IDS_DEFAULT_VALUE,
} from '../../common/ecmFPTITagsKeys';

export const UPDATE_CONTEXT_URL = '/smarthelp/api/update-context';
export const SET_CONTEXT_URL = '/smarthelp/api/set-context';
export const CHANNEL_NAME_BNA = 'BNA';

/**
 * Set OmniChannelAPITriggered value in session to not trigger SET context when update/set context is already triggered
 * @param value
 */
export const omniChannelAPITriggeredInSession = (value = false) => setInSession(OMNI_CHANNEL_API_SESSION_KEY, value);

export const getFPTIInfoByPath = (pathName) => {
  switch (pathName) {
    case pageNames.HOME: {
      return {
        initialFire: updateContextFPTIhome.updateContexthome,
        success: updateContextFPTIhome.updateContextSuccesshome,
        failure: updateContextFPTIhome.updateContextFailurehome,
        notFired: updateContextFPTIhome.updateContextNotFiredhome,
        nullIcfId: updateContextFPTIhome.updateContextFailureNullIcfIdhome,
      };
    }
    case pageNames.CONTACT_US: {
      return {
        initialFire: updateContextFPTIcontactUs.updateContextcontactUs,
        success: updateContextFPTIcontactUs.updateContextSuccesscontactUs,
        failure: updateContextFPTIcontactUs.updateContextFailurecontactUs,
        notFired: updateContextFPTIcontactUs.updateContextNotFiredcontactUs,
        nullIcfId: updateContextFPTIcontactUs.updateContextFailureNullIcfIdcontactUs,
      };
    }
    default: {
      return {};
    }
  }
};

export const getSetContextFPTIInfoByPath = (pathName) => {
  switch (pathName) {
    case pageNames.HOME: {
      return {
        initialFire: setContextFPTIhome.setContexthome,
        success: setContextFPTIhome.setContextSuccesshome,
        failure: setContextFPTIhome.setContextFailurehome,
        notFired: setContextFPTIhome.setContextNotFiredhome,
      };
    }
    default: {
      return {};
    }
  }
};

/**
 * Update context POST call with available intents information
 */
export const updateContext = (updateContextObj) => {
  const authFlowContextId = _get(updateContextObj, 'authFlowContextId', '');
  const stepUpRequestedTime = _get(updateContextObj, 'stepUpRequestedTime', '');
  const intents = _get(updateContextObj, 'articleContent.channelDetails', '');
  const customerConfirmed = _get(updateContextObj, 'customerConfirmed', '');
  const deviceType = getDeviceType();
  let pathName = _get(updateContextObj, 'pageName', pageNames.HOME); // TODO:: this pathname can be taken as a parameter from where this function is being initiated instead of a hardcoded value.

  // Dynamic fptiObject names(different for different pages). Since dynamic loading of the fpti object name results in error
  const fptiObject = getFPTIInfoByPath(pathName);

  // if icf id exists pass icf id else pass the article id
  const faqId = updateContextObj.icfId || updateContextObj.articleContent.articleId;

  // get the ECM API tags from article data
  const fptiDocId =
    (updateContextObj.articleContent && updateContextObj.articleContent[ECM_DOC_ID_KEY]) || ECM_DOC_ID_DEFAULT_VALUE;
  const fptiComponentIds =
    (updateContextObj.articleContent && updateContextObj.articleContent[ECM_COMPONENT_IDS_KEY]) ||
    ECM_COMPONENT_IDS_DEFAULT_VALUE;

  // Only if we have intents, contextId and faq call update context service
  if (isLoggedIn() && !_isEmpty(intents) && faqId) {
    // setting the session variable when omni channel API is triggered, only when customerConfirmed is true
    // customerConfirmed will be false only when NO is clicked on the omni channel prompt
    if (customerConfirmed) {
      omniChannelAPITriggeredInSession(true);
    }

    const timeStamp = Math.round(+new Date() / 1000);

    const updateContextReqParams = {
      _csrf: getCsrfToken(),
      intents,
      deviceType,
      faqId,
      customerConfirmed,
    };

    // If authFlowContextId and stepUpRequestedTime exists adding them to update context request params
    // these values are available once step up up is initiated
    // and after step up is done and while redirecting to help center page we will be making update context call by adding these params
    if (authFlowContextId && stepUpRequestedTime) {
      updateContextReqParams.authFlowContextId = authFlowContextId;
      updateContextReqParams.stepUpRequestedTime = stepUpRequestedTime;
      // changing the pathname to fire step up update context API.
      // eslint-disable-next-line no-unused-vars
      pathName = 'StepUp'; // TODO : Add FPTI pathname when the stepUp is ready
    }

    const initialFptiOptions = {
      ...fptiObject.initialFire,
      ocp_context_metadata: JSON.stringify(intents),
      timestamp: timeStamp,
      link_name: customerConfirmed,
      hpid: faqId,
      help_doc_id: fptiDocId,
      help_component_ids: fptiComponentIds,
    };

    // FPTI For update context
    trackActivityEvent(initialFptiOptions);

    return axios
      .post(UPDATE_CONTEXT_URL, updateContextReqParams)
      .then(() => {
        // Fpti when update context is success
        trackActivityEvent({
          ...fptiObject.success,
          ...{ hpid: faqId },
          help_doc_id: fptiDocId,
          help_component_ids: fptiComponentIds,
        });
      })
      .catch(() => {
        // Fpti when update context is failed
        trackActivityEvent({
          ...fptiObject.failure,
          ...{ hpid: faqId },
          help_doc_id: fptiDocId,
          help_component_ids: fptiComponentIds,
        });
      });
  }

  // if the required information is not  available for firing update context call, then firing an FPTI call
  return trackActivityEvent({
    ...fptiObject.notFired,
    ...{ hpid: faqId },
    help_doc_id: fptiDocId,
    help_component_ids: fptiComponentIds,
  });
};

/**
 * Function to get BNA-ID for the provided ICF-ID
 */
export const getBnaId = (icfId) => {
  const bnaMapping = getIcfConfigMappings() || {};
  const bnaId = [];
  Object.keys(bnaMapping).forEach((key) => {
    if (bnaMapping[key] === icfId) {
      // Pushing all the BNA's which match ICF articles
      bnaId.push(key);
    }
  });
  return bnaId;
};

/**
 * Function to get BNA article mapped to ICF and return the data for making update context API call
 * send customerConfirmed as true for the updateContext call
 */
export const icfArticleUpdateContextData = (icfArticle, articles) => {
  // set the empty BNA options data for update--context call
  const setBNAOptions = {
    articleContent: {},
    icfId: '',
    customerConfirmed: false,
  };

  const articleIcfId = (icfArticle && icfArticle.icf_id) || '';

  if (articleIcfId) {
    // get bnaId from article icfId
    const BnaId = getBnaId(articleIcfId);

    // checking for BNA ID in the list of BNA ids returned from icf remote config mapping and return the required bna data
    _forEach(articles, (article) => {
      if (article.channel === CHANNEL_NAME_BNA && BnaId.indexOf(article.channelDetails.bnaId) >= 0) {
        setBNAOptions.articleContent = article;
        setBNAOptions.icfId = articleIcfId;
        setBNAOptions.customerConfirmed = true;
        return false;
      }
      return true;
    });

    return setBNAOptions;
  }

  // if no icf id then log a FPTI indicating null icf id and return empty BNA options data
  const pathName = pageNames.HOME;
  const fptiObject = getFPTIInfoByPath(pathName);
  trackActivityEvent({ ...fptiObject.nullIcfId });

  return setBNAOptions;
};

/**
 * set context POST call with available intents from FAQ or Trackers data
 */
// export const setContext = (intents, faqId, customerConfirmed = false, isTrackersData = false, trackersData = {}) => {
export const setContext = (setContextObj) => {
  const intents = _get(setContextObj, 'intents', '');
  const faqId = _get(setContextObj, 'faqId', '');
  const customerConfirmed = _get(setContextObj, 'customerConfirmed', false);
  const isTrackersData = _get(setContextObj, 'isTrackersData', false);
  const trackersData = _get(setContextObj, 'trackersData', {});

  // get the ECM API tags from article data
  const fptiDocId =
    (setContextObj.articleContent && setContextObj.articleContent[ECM_DOC_ID_KEY]) || ECM_DOC_ID_DEFAULT_VALUE;
  const fptiComponentIds =
    (setContextObj.articleContent && setContextObj.articleContent[ECM_COMPONENT_IDS_KEY]) ||
    ECM_COMPONENT_IDS_DEFAULT_VALUE;

  // Dynamic fptiObject names(different for different pages). Since dynamic loading of the fpti object name results in error
  const pathName = pageNames.HOME;
  const fptiObject = getSetContextFPTIInfoByPath(pathName);

  // call setContext service only when we have intents AND faq OR trackers data
  if (isLoggedIn() && !_isEmpty(intents) && (faqId || (isTrackersData && trackersData))) {
    const deviceType = getDeviceType();
    const timeStamp = Math.round(+new Date() / 1000);

    const setContextReqParams = {
      _csrf: getCsrfToken(),
      intents,
      deviceType,
      faqId,
      isTrackersData,
      trackersData,
    };

    // send customerConfirmed only when true
    if (customerConfirmed) {
      setContextReqParams.customerConfirmed = customerConfirmed;
    }

    const initialFptiOptions = {
      ...fptiObject.initialFire,
      ocp_context_metadata: JSON.stringify(intents),
      timestamp: timeStamp,
      link_name: customerConfirmed,
      hpid: faqId,
      help_doc_id: fptiDocId,
      help_component_ids: fptiComponentIds,
    };

    // FPTI For set context
    trackActivityEvent(initialFptiOptions);

    return axios
      .post(SET_CONTEXT_URL, setContextReqParams)
      .then(() => {
        // Fpti when set context is success
        trackActivityEvent({
          ...fptiObject.success,
          ...{ hpid: faqId },
          help_doc_id: fptiDocId,
          help_component_ids: fptiComponentIds,
        });
      })
      .catch(() => {
        // Fpti when set context is failed
        trackActivityEvent({
          ...fptiObject.failure,
          ...{ hpid: faqId },
          help_doc_id: fptiDocId,
          help_component_ids: fptiComponentIds,
        });
      });
  }

  // FPTI when the required information is not available for firing set context call
  return trackActivityEvent({
    ...fptiObject.notFired,
    ...{ hpid: faqId },
    help_doc_id: fptiDocId,
    help_component_ids: fptiComponentIds,
  });
};
